import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Container,
  Paper,
  Snackbar,
  Alert,
  Rating,
} from '@mui/material';
import { styled } from '@mui/system';
import { submitPublicReview } from '../../apis/trial';
import StarIcon from '@mui/icons-material/Star';
import Navbar from '../mainPage/components/Navbar';

const StyledTextField = styled(TextField)(({ multiline }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '2px',
    transition: 'all 0.2s ease-in-out',
    height: multiline ? 'auto' : 38,
    alignItems: 'flex-start',

    '& fieldset': {
      border: '0.5px solid #e5e7eb',
    },
    '&:hover fieldset': {
      border: '0.5px solid #d1d5db',
    },
    '&.Mui-focused fieldset': {
      border: '0.6px solid #5965A6',
    },
  },
  '& .MuiInputBase-input': {
    padding: multiline ? '10px 14px' : '10px 14px',
    fontSize: '0.9rem',
    fontWeight: 400,
    color: '#374151',
  },
}));

const StyledButton = styled(Button)({
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: '0',
    height: '0',
    borderRadius: '50%',
    background: 'rgba(255, 255, 255, 0.3)',
    transform: 'translate(-50%, -50%)',
    transition: 'width 0.6s, height 0.6s',
  },
  '&:hover::before': {
    width: '300px',
    height: '300px',
  },
});

const FeedbackPage = () => {
  const [mousePosition, setMousePosition] = useState({ x: 50, y: 50 });
  const [form, setForm] = useState({
    name: '',
    email: '',
    review_text: '',
    rating: 0,
    organization: '',
  });
  const [errors, setErrors] = useState({
    name: '',
    email: '',
    organization: '',
    rating: '',
  });
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState({
    open: false,
    type: 'success',
    msg: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((e) => ({ ...e, [name]: '' }));
    }
  };

  const handleRatingChange = (event, newValue) => {
    setForm((f) => ({ ...f, rating: newValue || 0 }));
    // Clear error when user selects a rating
    if (errors.rating) {
      setErrors((e) => ({ ...e, rating: '' }));
    }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = () => {
    const newErrors = {
      name: '',
      email: '',
      organization: '',
      rating: '',
    };
    let isValid = true;

    // Validate name
    if (!form.name.trim()) {
      newErrors.name = 'Name is required';
      isValid = false;
    }

    // Validate email
    if (!form.email.trim()) {
      newErrors.email = 'Email is required';
      isValid = false;
    } else if (!validateEmail(form.email)) {
      newErrors.email = 'Please enter a valid email address';
      isValid = false;
    }

    // Validate organization (Job Title)
    if (!form.organization.trim()) {
      newErrors.organization = 'Job Title is required';
      isValid = false;
    }

    // Validate rating
    if (form.rating === 0) {
      newErrors.rating = 'Please provide a rating';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate all fields
    if (!validateForm()) {
      setNotice({
        open: true,
        type: 'error',
        msg: 'Please fill in all required fields correctly.',
      });
      return;
    }

    setLoading(true);
    try {
      await submitPublicReview({
        name: form.name,
        email: form.email,
        rating: form.rating,
        review_text: form.review_text,
        organization: form.organization,
      });

      setNotice({
        open: true,
        type: 'success',
        msg: 'Thank you for your feedback!',
      });

      // Reset form and errors
      setForm({
        name: '',
        email: '',
        review_text: '',
        rating: 0,
        organization: '',
      });
      setErrors({ name: '', email: '', organization: '', rating: '' });
    } catch (err) {
      console.error(err);
      setNotice({
        open: true,
        type: 'error',
        msg:
          err.response?.data?.message ||
          'Something went wrong. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      const feedbackSection = document.getElementById('feedback-section');
      if (feedbackSection) {
        const rect = feedbackSection.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        setMousePosition({
          x: Math.max(0, Math.min(100, x)),
          y: Math.max(0, Math.min(100, y)),
        });
      }
    };

    const feedbackSection = document.getElementById('feedback-section');
    if (feedbackSection) {
      feedbackSection.addEventListener('mousemove', handleMouseMove);
      return () =>
        feedbackSection.removeEventListener('mousemove', handleMouseMove);
    }
  }, []);

  return (
    <>
      <Navbar hideLoginButton={false} enableScrollEffect={true} />

      <Box
        id="feedback-section"
        sx={{
          width: '100vw',
          height: '100vh',
          background: '#3044A9',
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          cursor: 'none',
          pt: { xs: 10, sm: 12 },
          pb: 7,
        }}
      >
        {/* Enhanced Grid Pattern Background - Full Coverage */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1,
            backgroundImage: `
            linear-gradient(rgba(255, 255, 255, 0.15) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255, 255, 255, 0.15) 1px, transparent 1px)
          `,
            backgroundSize: '80px 80px',
            opacity: 0.25,
          }}
        />

        {/* Cursor Following Grid Highlight */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 2,
            backgroundImage: `
            linear-gradient(rgba(255, 255, 255, 0.3) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255, 255, 255, 0.3) 1px, transparent 1px)
          `,
            backgroundSize: '80px 80px',
            maskImage: `radial-gradient(350px circle at ${mousePosition.x}% ${mousePosition.y}%, white, transparent)`,
            WebkitMaskImage: `radial-gradient(350px circle at ${mousePosition.x}% ${mousePosition.y}%, white, transparent)`,
            transition:
              'mask-image 0.1s ease-out, -webkit-mask-image 0.1s ease-out',
          }}
        />

        {/* Fine Grid Overlay */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1,
            backgroundImage: `
            linear-gradient(rgba(255, 255, 255, 0.08) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255, 255, 255, 0.08) 1px, transparent 1px)
          `,
            backgroundSize: '20px 20px',
            opacity: 0.2,
          }}
        />

        {/* Cursor Following Fine Grid */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 2,
            backgroundImage: `
            linear-gradient(rgba(255, 255, 255, 0.4) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255, 255, 255, 0.4) 1px, transparent 1px)
          `,
            backgroundSize: '20px 20px',
            maskImage: `radial-gradient(200px circle at ${mousePosition.x}% ${mousePosition.y}%, white, transparent)`,
            WebkitMaskImage: `radial-gradient(200px circle at ${mousePosition.x}% ${mousePosition.y}%, white, transparent)`,
            transition:
              'mask-image 0.1s ease-out, -webkit-mask-image 0.1s ease-out',
          }}
        />

        {/* Cursor Glow Effect */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 3,
            background: `radial-gradient(400px circle at ${mousePosition.x}% ${mousePosition.y}%, rgba(255, 255, 255, 0.06) 0%, transparent 70%)`,
            transition: 'background 0.2s ease-out',
            pointerEvents: 'none',
          }}
        />

        {/* Animated Grid Overlay */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1,
            backgroundImage: `
            radial-gradient(circle at 20% 30%, rgba(255, 255, 255, 0.08) 0%, transparent 50%),
            radial-gradient(circle at 80% 70%, rgba(255, 255, 255, 0.06) 0%, transparent 50%),
            radial-gradient(circle at 40% 80%, rgba(255, 255, 255, 0.04) 0%, transparent 50%)
          `,
            animation: 'gridPulse 8s ease-in-out infinite',
            '@keyframes gridPulse': {
              '0%, 100%': {
                opacity: 0.3,
              },
              '50%': {
                opacity: 0.6,
              },
            },
          }}
        />

        {/* Depth and Glow Effects */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1,
            background: `
            radial-gradient(ellipse at center, rgba(255, 255, 255, 0.02) 0%, transparent 70%),
            linear-gradient(45deg, rgba(255, 255, 255, 0.03) 0%, transparent 50%),
            linear-gradient(-45deg, rgba(255, 255, 255, 0.02) 0%, transparent 50%)
          `,
            '&::before': {
              content: '""',
              position: 'absolute',
              top: '10%',
              left: '10%',
              right: '10%',
              bottom: '10%',
              background:
                'radial-gradient(ellipse at center, rgba(255, 255, 255, 0.05) 0%, transparent 60%)',
              borderRadius: '50%',
              filter: 'blur(40px)',
              animation: 'float 6s ease-in-out infinite',
            },
            '@keyframes float': {
              '0%, 100%': {
                transform: 'translateY(0px) scale(1)',
                opacity: 0.3,
              },
              '50%': {
                transform: 'translateY(-10px) scale(1.05)',
                opacity: 0.5,
              },
            },
          }}
        />

        <Container
          maxWidth="sm"
          disableGutters
          sx={{
            minHeight: { xs: '100dvh', md: '90vh', lg: '90vh' },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#3044A9',
            position: 'relative',
            zIndex: 10,
          }}
        >
          <Paper
            elevation={6}
            sx={{
              width: '100%',
              maxWidth: 700,
              borderRadius: 1.5,
              p: { xs: 3, sm: 4, md: 5 },
              bgcolor: '#f2f2f2',
            }}
          >
            <Typography
              variant="h6"
              align="center"
              sx={{ fontWeight: 700, mb: 1 }}
            >
              Share Your Feedback
              <br />
              Help Us Improve
            </Typography>

            <Typography
              variant="body2"
              align="center"
              sx={{ color: 'text.secondary', mb: 3 }}
            >
              We value your opinion! Please share your experience and help us
              serve you better.
            </Typography>

            <Box component="form" noValidate onSubmit={handleSubmit}>
              <Stack spacing={2}>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary' }}
                  >
                    Full Name <span style={{ color: 'red' }}>*</span>
                  </Typography>
                  <StyledTextField
                    fullWidth
                    size="small"
                    placeholder="Enter Full Name"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    required
                    error={!!errors.name}
                    helperText={errors.name}
                    inputProps={{ 'aria-label': 'Full Name' }}
                  />
                </Box>

                <Box>
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary' }}
                  >
                    Email <span style={{ color: 'red' }}>*</span>
                  </Typography>
                  <StyledTextField
                    fullWidth
                    size="small"
                    placeholder="Enter Email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                    error={!!errors.email}
                    helperText={errors.email}
                    inputProps={{ 'aria-label': 'Email' }}
                  />
                </Box>

                <Box>
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary' }}
                  >
                    Job Title <span style={{ color: 'red' }}>*</span>
                  </Typography>
                  <StyledTextField
                    fullWidth
                    size="small"
                    placeholder="e.g. Manager at Infotech"
                    name="organization"
                    value={form.organization}
                    onChange={handleChange}
                    required
                    error={!!errors.organization}
                    helperText={errors.organization}
                    inputProps={{ 'aria-label': 'Job Title' }}
                  />
                </Box>

                <Box>
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary', mb: 1, display: 'block' }}
                  >
                    Rating <span style={{ color: 'red' }}>*</span>
                  </Typography>
                  <Rating
                    name="rating"
                    value={form.rating}
                    onChange={handleRatingChange}
                    size="large"
                    icon={<StarIcon sx={{ color: '#FFD700' }} />}
                    emptyIcon={<StarIcon sx={{ color: '#e0e0e0' }} />}
                    sx={{
                      '& .MuiRating-iconFilled': {
                        color: '#FFD700',
                      },
                      '& .MuiRating-iconHover': {
                        color: '#FFC107',
                      },
                    }}
                  />
                  {errors.rating && (
                    <Typography
                      variant="caption"
                      sx={{ color: 'error.main', display: 'block', mt: 0.5 }}
                    >
                      {errors.rating}
                    </Typography>
                  )}
                </Box>

                <Box>
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary' }}
                  >
                    Your Feedback
                  </Typography>
                  <StyledTextField
                    fullWidth
                    size="small"
                    placeholder="Share your thoughts, suggestions, or report any issues..."
                    name="review_text"
                    value={form.review_text}
                    onChange={handleChange}
                    multiline
                    minRows={3}
                  />
                </Box>

                <StyledButton
                  type="submit"
                  fullWidth
                  disabled={loading}
                  sx={{
                    mt: 1,
                    py: 1.5,
                    fontWeight: 700,
                    fontSize: '1rem',
                    color: '#fff',
                    bgcolor: '#FF6B35',
                    background:
                      'linear-gradient(135deg, #FF6B35 0%, #F7931E 100%)',
                    boxShadow: '0 4px 15px rgba(255, 107, 53, 0.4)',
                    animation:
                      'pulseGlow 2s ease-in-out infinite, bounce 2s ease-in-out infinite',
                    '&:hover': {
                      bgcolor: '#FF5722',
                      background:
                        'linear-gradient(135deg, #FF5722 0%, #FF6B35 100%)',
                      boxShadow: '0 6px 20px rgba(255, 107, 53, 0.6)',
                      transform: 'translateY(-2px)',
                    },
                    '&:disabled': {
                      bgcolor: '#ccc',
                      background: '#ccc',
                    },
                    opacity: loading ? 0.8 : 1,
                    transition: 'all 0.3s ease',
                    '@keyframes pulseGlow': {
                      '0%, 100%': {
                        boxShadow: '0 4px 15px rgba(255, 107, 53, 0.4)',
                      },
                      '50%': {
                        boxShadow:
                          '0 4px 25px rgba(255, 107, 53, 0.7), 0 0 30px rgba(255, 107, 53, 0.3)',
                      },
                    },
                    '@keyframes bounce': {
                      '0%, 100%': {
                        transform: 'translateY(0)',
                      },
                      '50%': {
                        transform: 'translateY(-5px)',
                      },
                    },
                  }}
                >
                  {loading ? 'Submitting…' : 'Send Feedback'}
                </StyledButton>

                <Typography
                  variant="caption"
                  align="center"
                  sx={{ color: 'text.secondary', fontSize: '0.7rem', mt: 0.5 }}
                >
                  By submitting, you agree to our <b>Privacy Policy</b>.
                </Typography>
              </Stack>
            </Box>
          </Paper>
          <Snackbar
            open={notice.open}
            autoHideDuration={2000}
            onClose={() => setNotice((n) => ({ ...n, open: false }))}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert
              onClose={() => setNotice((n) => ({ ...n, open: false }))}
              severity={notice.type}
              variant="filled"
              sx={{ width: '100%' }}
            >
              {notice.msg}
            </Alert>
          </Snackbar>
        </Container>
      </Box>
    </>
  );
};

export default FeedbackPage;
